import { useEffect, useState } from 'react';
import { setupTelemetry } from '../core/setup';
import { isTelemetrySdkInitialized } from '../core/telemetrySdk';
import { registerUnloadFlush } from '../core/unloadFlush';
import type React from 'react';
import type { TelemetryConfig } from '../types';

export type TelemetryProviderProps = {
  config: TelemetryConfig;
  children: React.ReactNode;
};

/**
 * React binding that initializes telemetry once. The provider intentionally
 * does NOT shut the SDK down on unmount: telemetry lifecycle is tied to the
 * page, not to a React subtree. Because the SDK is a process-wide singleton,
 * the provider guards re-initialization (e.g. remounts, multiple providers) so
 * it never trips `setupTelemetry`'s throw-on-re-init.
 *
 * An init failure is logged and re-thrown rather than swallowed: silently
 * returning would hide a broken app behind a console message nothing observes,
 * so the error propagates for an error boundary (or the surrounding app) to
 * handle.
 */
export const TelemetryProvider = ({ config, children }: TelemetryProviderProps) => {
  // Lazy initializer runs once per mount; the singleton guard keeps repeated
  // mounts from re-initializing (which would otherwise throw).
  useState(() => {
    try {
      if (!isTelemetrySdkInitialized()) {
        setupTelemetry(config);
      }
    } catch (error) {
      console.error('[telemetry] initialization failed', error);
      throw error;
    }
    return null;
  });

  // Register pagehide / visibilitychange→hidden listeners that forceFlush the
  // SDK when the page is about to unload. This runs after the useState
  // initializer above (and after React commits the render), so web-vitals'
  // own visibility listeners are already registered — ensuring metric values
  // are finalized before we flush. The returned unregister is the cleanup.
  useEffect(() => registerUnloadFlush(), []);

  return children;
};
